/**
 * Error Boundary component - Catches and handles React rendering errors.
 *
 * Design: DENiZEN
 * - Soft borders with rounded corners
 * - Subtle shadows
 * - High contrast
 * - WCAG 2.1 AA compliant
 */

import { Component, type ReactNode, type ErrorInfo } from 'react'

export interface ErrorBoundaryProps {
  /**
   * Child components to render
   */
  children: ReactNode
  /**
   * Optional custom fallback UI
   */
  fallback?: ReactNode
  /**
   * Optional callback when an error is caught
   */
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  /**
   * Optional reset key - when this changes, the error state is reset
   */
  resetKey?: string | number
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo })

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    // Log error in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error)
      console.error('Component stack:', errorInfo.componentStack)
    }
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Reset error state when resetKey changes
    if (
      this.state.hasError &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
      })
    }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  handleReload = (): void => {
    window.location.reload()
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default DENiZEN error UI
      return (
        <div
          className="min-h-screen flex items-center justify-center p-4 bg-white"
          role="alert"
          aria-live="assertive"
        >
          <div className="w-full max-w-lg">
            {/* Error card - DENiZEN */}
            <div className="bg-white border border-dark-green/15 shadow-denizen p-8 relative rounded-2xl">
              {/* Corner label */}
              <div className="pebble-label bg-error text-white text-xs mb-6">
                OOPS!
              </div>

              {/* Error icon */}
              <div className="w-16 h-16 mx-auto mb-6 bg-error border border-dark-green/15 shadow-denizen-sm rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-dark-green tracking-tight text-center mb-4">
                Something went{' '}
                <span className="bg-error text-white px-2 rounded-full">wrong</span>
              </h1>

              {/* Description */}
              <p className="text-center font-body text-muted mb-6">
                We encountered an unexpected error. Don't worry, you can try again
                or refresh the page.
              </p>

              {/* Error details (development only) */}
              {import.meta.env.DEV && this.state.error && (
                <details className="mb-6">
                  <summary className="cursor-pointer text-xs font-display font-bold text-dark-green tracking-wider mb-2 hover:text-muted transition-colors">
                    Error Details
                  </summary>
                  <div className="p-4 bg-white border border-dark-green/10 overflow-auto max-h-48 rounded-2xl">
                    <pre className="text-xs font-mono text-dark-green whitespace-pre-wrap break-words">
                      {this.state.error.toString()}
                      {this.state.errorInfo?.componentStack && (
                        <>
                          {'\n\nComponent Stack:'}
                          {this.state.errorInfo.componentStack}
                        </>
                      )}
                    </pre>
                  </div>
                </details>
              )}

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Try again button */}
                <button
                  type="button"
                  onClick={this.handleReset}
                  className="btn-denizen-primary flex-1 py-4 px-6"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="w-5 h-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9.53 2.47a.75.75 0 0 1 0 1.06L4.81 8.25H15a6.75 6.75 0 0 1 0 13.5h-3a.75.75 0 0 1 0-1.5h3a5.25 5.25 0 1 0 0-10.5H4.81l4.72 4.72a.75.75 0 1 1-1.06 1.06l-6-6a.75.75 0 0 1 0-1.06l6-6a.75.75 0 0 1 1.06 0Z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Try Again
                  </span>
                </button>

                {/* Refresh button */}
                <button
                  type="button"
                  onClick={this.handleReload}
                  className="btn-denizen-secondary flex-1 py-4 px-6"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="w-5 h-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.755 10.059a7.5 7.5 0 0 1 12.548-3.364l1.903 1.903h-3.183a.75.75 0 1 0 0 1.5h4.992a.75.75 0 0 0 .75-.75V4.356a.75.75 0 0 0-1.5 0v3.18l-1.9-1.9A9 9 0 0 0 3.306 9.67a.75.75 0 1 0 1.45.388Zm15.408 3.352a.75.75 0 0 0-.919.53 7.5 7.5 0 0 1-12.548 3.364l-1.902-1.903h3.183a.75.75 0 0 0 0-1.5H2.984a.75.75 0 0 0-.75.75v4.992a.75.75 0 0 0 1.5 0v-3.18l1.9 1.9a9 9 0 0 0 15.059-4.035.75.75 0 0 0-.53-.918Z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Refresh Page
                  </span>
                </button>
              </div>
            </div>

            {/* Help text */}
            <p className="mt-4 text-center text-xs font-body text-muted">
              If the problem persists, please try clearing your browser cache or
              contact support.
            </p>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
